import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import {
  CompanyProfile,
  RoundOffMode,
  settingsAPI,
} from "../../services/settingsAPI";
import financialYearAPI, {
  formatFinancialYearDisplay,
} from "../../services/financialYearAPI";

const emptyCompanyProfile = (): CompanyProfile => ({
  legalName: "",
  gstin: "",
  pan: "",
  stateCode: "",
});

type SettingsTab =
  | "company"
  | "financial"
  | "tax"
  | "billing"
  | "inventory"
  | "nfc"
  | "security";

const SettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<SettingsTab>("company");
  const [amount, setAmount] = useState("");
  const [company, setCompany] = useState<CompanyProfile>(emptyCompanyProfile);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [defaultTaxInclusive, setDefaultTaxInclusive] = useState(false);
  const [roundOffMode, setRoundOffMode] = useState<RoundOffMode>("none");
  const [maxDiscountPercent, setMaxDiscountPercent] = useState("0");
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState("0");
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: () => settingsAPI.getSettings(),
  });

  const { data: financialYears = [], isLoading: fyLoading } = useQuery({
    queryKey: ["financialYears"],
    queryFn: () => financialYearAPI.list(),
  });

  const fyAction = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "close" | "reopen" }) =>
      action === "close"
        ? financialYearAPI.close(id)
        : financialYearAPI.reopen(id),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["financialYears"] });
      setAlert({
        type: "success",
        message:
          vars.action === "close"
            ? "Financial year closed — posting locked"
            : "Financial year reopened",
      });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message:
          err.response?.data?.message ||
          err.message ||
          "FY action failed",
      });
    },
  });

  useEffect(() => {
    if (data != null) {
      setAmount(String(data.cardSecurityAmount ?? 0));
      setCompany(data.companyProfile ?? emptyCompanyProfile());
      setAllowNegativeStock(Boolean(data.allowNegativeStock));
      setDefaultTaxInclusive(Boolean(data.defaultTaxInclusive));
      setRoundOffMode(data.roundOffMode || "none");
      setMaxDiscountPercent(String(data.maxDiscountPercent ?? 0));
      setSessionTimeoutMinutes(String(data.sessionTimeoutMinutes ?? 0));
    }
  }, [data]);

  const { mutate: saveSettings, isPending: saving } = useMutation({
    mutationFn: () =>
      settingsAPI.updateSettings({
        cardSecurityAmount: Number(amount) || 0,
        companyProfile: company,
        allowNegativeStock,
        defaultTaxInclusive,
        roundOffMode,
        maxDiscountPercent: Number(maxDiscountPercent) || 0,
        sessionTimeoutMinutes: Number(sessionTimeoutMinutes) || 0,
      }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      setAmount(String(updated.cardSecurityAmount));
      if (updated.companyProfile) {
        setCompany(updated.companyProfile);
      }
      if (updated.allowNegativeStock !== undefined) {
        setAllowNegativeStock(Boolean(updated.allowNegativeStock));
      }
      if (updated.defaultTaxInclusive !== undefined) {
        setDefaultTaxInclusive(Boolean(updated.defaultTaxInclusive));
      }
      if (updated.roundOffMode) setRoundOffMode(updated.roundOffMode);
      if (updated.maxDiscountPercent !== undefined) {
        setMaxDiscountPercent(String(updated.maxDiscountPercent));
      }
      if (updated.sessionTimeoutMinutes !== undefined) {
        setSessionTimeoutMinutes(String(updated.sessionTimeoutMinutes));
      }
      setAlert({ type: "success", message: "Settings saved successfully." });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to save settings",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAlert(null);
    const value = Number(amount);
    if (!Number.isFinite(value) || value < 0) {
      setAlert({ type: "error", message: "Enter a valid amount (0 or greater)." });
      return;
    }
    saveSettings();
  };

  const updateCompany = (field: keyof CompanyProfile, value: string) => {
    setCompany((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box>
      <PageHeader
        title="Settings"
        subtitle="System configuration (Super Admin only)"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Settings" },
        ]}
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || "Failed to load settings"}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Tabs
            value={tab}
            onChange={(_e, value: SettingsTab) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab value="company" label="Company & GST" />
            <Tab value="financial" label="Financial year" />
            <Tab value="tax" label="Tax" />
            <Tab value="billing" label="Billing" />
            <Tab value="inventory" label="Inventory" />
            <Tab value="nfc" label="NFC security" />
            <Tab value="security" label="Security" />
          </Tabs>

          {tab === "company" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Company / GST profile
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Used on GST reports, invoices, and Tally export. Stored in app settings
                (not the disabled companies module).
              </Typography>

              <Grid container spacing={2} maxWidth={720}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Legal name"
                    value={company.legalName}
                    onChange={(e) => updateCompany("legalName", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="GSTIN"
                    value={company.gstin}
                    onChange={(e) => updateCompany("gstin", e.target.value.toUpperCase())}
                    inputProps={{ maxLength: 15 }}
                    helperText="15-character GST identification number"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PAN"
                    value={company.pan}
                    onChange={(e) => updateCompany("pan", e.target.value.toUpperCase())}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="State code"
                    value={company.stateCode}
                    onChange={(e) => updateCompany("stateCode", e.target.value)}
                    inputProps={{ maxLength: 2 }}
                    helperText="e.g. 27 for Maharashtra"
                  />
                </Grid>
              </Grid>

              <Box mt={3}>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={() => {
                    setAlert(null);
                    saveSettings();
                  }}
                >
                  {saving ? "Saving…" : "Save company profile"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          )}

          {tab === "financial" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Financial year
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Close a year to lock new bill / purchase / recharge / voucher
                numbers for that FY. Super Admin only.
              </Typography>
              {fyLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Year</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {financialYears.map((fy) => (
                      <TableRow key={fy.id}>
                        <TableCell>
                          {formatFinancialYearDisplay({
                            ...fy,
                            isClosed: false,
                          })}
                          {fy.isCurrent ? (
                            <Chip
                              size="small"
                              label="Current"
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {fy.startDate} → {fy.endDate}
                        </TableCell>
                        <TableCell>
                          {fy.isClosed ? (
                            <Chip size="small" label="Closed" color="warning" />
                          ) : (
                            <Chip size="small" label="Open" color="success" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {fy.isClosed ? (
                            <Button
                              size="small"
                              disabled={fyAction.isPending}
                              onClick={() => {
                                if (
                                  !window.confirm(
                                    `Reopen ${fy.code}? Posting will be allowed again.`
                                  )
                                ) {
                                  return;
                                }
                                fyAction.mutate({
                                  id: fy.id,
                                  action: "reopen",
                                });
                              }}
                            >
                              Reopen
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              color="warning"
                              disabled={fyAction.isPending}
                              onClick={() => {
                                if (
                                  !window.confirm(
                                    `Close ${fy.code}? New documents for this FY will be blocked.`
                                  )
                                ) {
                                  return;
                                }
                                fyAction.mutate({
                                  id: fy.id,
                                  action: "close",
                                });
                              }}
                            >
                              Close
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          )}

          {tab === "tax" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Tax defaults
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Company-wide defaults for new products and bill totals. Sales-point
                tax mode (CGST/SGST vs IGST) still overrides per counter.
              </Typography>
              <Stack spacing={2} maxWidth={480}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={defaultTaxInclusive}
                      onChange={(e) => setDefaultTaxInclusive(e.target.checked)}
                    />
                  }
                  label="Default prices are tax-inclusive"
                />
                <FormControl fullWidth>
                  <InputLabel id="round-off-label">Bill round-off</InputLabel>
                  <Select
                    labelId="round-off-label"
                    label="Bill round-off"
                    value={roundOffMode}
                    onChange={(e) =>
                      setRoundOffMode(e.target.value as RoundOffMode)
                    }
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="0.05">Nearest ₹0.05</MenuItem>
                    <MenuItem value="1.00">Nearest ₹1</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={() => saveSettings()}
                >
                  {saving ? "Saving…" : "Save tax settings"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          )}

          {tab === "billing" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Billing policy
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Caps and policies for POS discounts. Electron can also enforce a
                discount PIN threshold separately.
              </Typography>
              <Stack spacing={2} maxWidth={420}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max discount (%)"
                  value={maxDiscountPercent}
                  onChange={(e) => setMaxDiscountPercent(e.target.value)}
                  inputProps={{ min: 0, max: 100, step: "0.01" }}
                  helperText="0 = no company-wide cap recorded (PIN rules still apply on POS)"
                />
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={() => saveSettings()}
                >
                  {saving ? "Saving…" : "Save billing settings"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          )}

          {tab === "inventory" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Inventory
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowNegativeStock}
                    onChange={(e) => setAllowNegativeStock(e.target.checked)}
                  />
                }
                label="Allow negative stock (disable guard on sale/adjust)"
              />
              <Box mt={3}>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={() => {
                    setAlert(null);
                    saveSettings();
                  }}
                >
                  {saving ? "Saving…" : "Save inventory settings"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          )}

          {tab === "nfc" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                NFC card security
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                When a physical card receives its first recharge, this amount is deducted once
                as a security deposit. Two ledger entries are created: recharge credit and
                security debit. The card will not be charged again on later recharges.
              </Typography>

              <Box component="form" onSubmit={handleSubmit} maxWidth={420}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Card security amount (₹)"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputProps={{ min: 0, step: "0.01" }}
                  helperText="Set to 0 to disable security deduction"
                />
                <Box mt={3}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      saving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save settings"}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
          )}

          {tab === "security" && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Session security
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Website idle timeout for Super Admin sessions. POS auto-lock is
                configured in Electron Settings → Security. Two-factor auth for
                Super Admin is under Profile → Two-factor auth.
              </Typography>
              <Stack spacing={2} maxWidth={420}>
                <TextField
                  fullWidth
                  type="number"
                  label="Session timeout (minutes)"
                  value={sessionTimeoutMinutes}
                  onChange={(e) => setSessionTimeoutMinutes(e.target.value)}
                  inputProps={{ min: 0, max: 1440, step: 1 }}
                  helperText="0 = disabled · max 1440 (24 hours)"
                />
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={saving}
                  onClick={() => saveSettings()}
                >
                  {saving ? "Saving…" : "Save security settings"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default SettingsPage;
